import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { Note } from '../types';
import { showToast } from '../utils/toast';

type DbNote = Database['public']['Tables']['notes']['Row'];
type DbNoteInsert = Database['public']['Tables']['notes']['Insert'];
type DbNoteUpdate = Database['public']['Tables']['notes']['Update'];

const isValidUUID = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
const safeUUID = (id?: string | null) => (id && isValidUUID(id) ? id : null);

const mapToNote = (dbNote: DbNote): Note => ({
  id: dbNote.id,
  title: dbNote.title,
  content: dbNote.content || '',
  domainId: dbNote.domain_id || '',
  date: dbNote.created_at
    ? new Date(dbNote.created_at).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0],
  updatedAt: dbNote.updated_at || undefined,
  tags: dbNote.tags || [],
  eventId: dbNote.event_id || undefined,
  taskId: dbNote.task_id || undefined,
  goalId: dbNote.goal_id || undefined,
  linkedEntityIds: [], // Non supporté nativement par DB relationnelle (remplacé par relations fortes comme event_id)
});

export const noteService = {
  async getNotes(): Promise<Note[]> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Vous devez être connecté.');

      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[note.service] getNotes:', error.message);
        showToast('Erreur lors du chargement des notes', 'error');
        return [];
      }
      return (data || []).map(mapToNote);
    } catch (err: any) {
      console.error('[note.service] getNotes:', err.message);
      showToast('Erreur lors du chargement des notes', 'error');
      return [];
    }
  },

  async createNote(note: Partial<Note>): Promise<Note> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Vous devez être connecté.');

    const insertData: DbNoteInsert = {
      user_id: user.id,
      title: note.title || 'Nouvelle note',
      content: note.content || null,
      domain_id: safeUUID(note.domainId),
      event_id: safeUUID(note.eventId),
      task_id: safeUUID(note.taskId),
      goal_id: safeUUID(note.goalId),
      tags: note.tags || [],
      is_archived: false,
    };

    const { data, error } = await supabase.from('notes').insert(insertData).select().single();

    if (error) {
      console.error('[note.service] createNote:', error.message);
      showToast('Impossible de créer la note', 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer la note : le domaine ou l'événement lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }
    return mapToNote(data);
  },

  async updateNote(id: string, updates: Partial<Note>): Promise<Note> {
    const updateData: DbNoteUpdate = {
      ...(updates.title !== undefined && { title: updates.title }),
      ...(updates.content !== undefined && { content: updates.content }),
      ...(updates.domainId !== undefined && { domain_id: safeUUID(updates.domainId) }),
      ...(updates.eventId !== undefined && { event_id: safeUUID(updates.eventId) }),
      ...(updates.taskId !== undefined && { task_id: safeUUID(updates.taskId) }),
      ...(updates.goalId !== undefined && { goal_id: safeUUID(updates.goalId) }),
      ...(updates.tags !== undefined && { tags: updates.tags }),
      updated_at: new Date().toISOString(),
    };

    if ('isArchived' in updates) {
      updateData.is_archived = (updates as any).isArchived;
    }

    const { data, error } = await supabase
      .from('notes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('[note.service] updateNote:', error.message);
      showToast('Impossible de mettre à jour la note', 'error');
      if (error.code === '23503') {
        throw new Error(
          "Impossible d'enregistrer la note : le domaine ou l'événement lié n'existe pas encore dans la base de données Supabase."
        );
      }
      throw error;
    }
    return mapToNote(data);
  },

  async deleteNote(id: string): Promise<void> {
    const { error } = await supabase.from('notes').delete().eq('id', id);

    if (error) {
      console.error('[note.service] deleteNote:', error.message);
      showToast('Impossible de supprimer la note', 'error');
      throw error;
    }
  },
};
